import { useMemo } from "react";
import { useRoute, Link, useSearch } from "wouter";
import { Loader2, ArrowLeft, FileText } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useFeatureFlag, useInsured, useInsuredPolicies } from "@/lib/api";
import { FLAG_AOR_EXPERIENCE } from "@shared/feature-flags";
import { useAgencyContext } from "@/lib/agencyContext";
import { InsuredSidebar } from "@/components/insured/InsuredSidebar";
import {
  InsuredTabs,
  INSURED_TABS,
  isInsuredTabKey,
  type InsuredTabKey,
} from "@/components/insured/InsuredTabs";
import { TabEmptyState } from "@/components/insured/TabEmptyState";
import { OverviewTab } from "@/components/insured/tabs/OverviewTab";
import { PoliciesTab } from "@/components/insured/tabs/PoliciesTab";
import { AssetsTab } from "@/components/insured/tabs/AssetsTab";
import { COIsTab } from "@/components/insured/tabs/COIsTab";
import { DocumentsTab } from "@/components/insured/documents/DocumentsTab";

export default function InsuredDetailNew() {
  const [, params] = useRoute("/insureds/:id");
  const id = params?.id ?? "";
  const search = useSearch();
  const { data: insured, isLoading: insuredLoading } = useInsured(id);
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;
  // CSR-674: dedicated, capped (500) endpoint replaces the bulk-list path.
  // The server already filters out soft-deleted rows.
  const {
    data: policiesData,
    isLoading: policiesLoading,
    isSuccess: policiesLoaded,
  } = useInsuredPolicies(insured?.id ?? null, agencyId);
  const isLoading = insuredLoading || policiesLoading;
  // CSR-2327 — every AOR surface on this page is behind one flag, read here.
  const showAor = useFeatureFlag(FLAG_AOR_EXPERIENCE);

  const insuredPolicies = useMemo(() => policiesData?.data ?? [], [policiesData?.data]);

  if (isLoading) {
    return (
      <Layout fullWidth>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!insured) {
    return (
      <Layout fullWidth>
        <div className="p-6">
          <Link href="/insureds">
            <Button variant="ghost" className="gap-2 pl-0 hover:pl-2 transition-all">
              <ArrowLeft className="w-4 h-4" /> Back to Insureds
            </Button>
          </Link>
          <Card className="mt-6">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-lg font-medium">Insured not found</p>
              <p className="text-sm text-muted-foreground mt-1">
                No insured exists with this id.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const searchParams = new URLSearchParams(search);
  const tabParam = searchParams.get("tab") ?? "";
  const activeTab: InsuredTabKey = isInsuredTabKey(tabParam) ? tabParam : "overview";

  return (
    <Layout fullWidth>
      <div
        className="flex flex-col md:flex-row min-h-[calc(100vh-3.5rem)] w-full"
        data-testid="insured-detail-new"
      >
        <InsuredSidebar
          insured={insured}
          policies={insuredPolicies}
          policiesLoaded={policiesLoaded}
          showAor={showAor}
        />
        <section className="flex-1 min-w-0">
          <InsuredTabs active={activeTab} />
          <div
            id="insured-tab-panel"
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            className="px-4 md:px-6 py-6"
          >
            {activeTab === "overview" ? (
              <OverviewTab
                insured={insured}
                policies={insuredPolicies}
                showAor={showAor}
              />
            ) : activeTab === "policies" ? (
              <PoliciesTab
                insured={insured}
                policies={insuredPolicies}
                showAor={showAor}
              />
            ) : activeTab === "cois" ? (
              <COIsTab
                insured={insured}
                policies={insuredPolicies}
                showAor={showAor}
              />
            ) : activeTab === "assets" ? (
              <AssetsTab insured={insured} policies={insuredPolicies} />
            ) : activeTab === "documents" ? (
              <DocumentsTab insured={insured} />
            ) : (
              <TabEmptyState
                tabLabel={INSURED_TABS.find((t) => t.key === activeTab)?.label ?? activeTab}
              />
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
